import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';

const getUrlParams = () => {
  const params = new URLSearchParams(window.location.hash.substr(1));
  return params;
};

const mergeUrlParams: (urlParams: any, data: any) => void = (urlParams, data) => {
  Object.entries(data).forEach(([key, val]) => {
    if (_.isEmpty(val)) {
      urlParams.delete(key);
    } else {
      let v = val;
      if (typeof val === 'object' && val !== null) {
        v = JSON.stringify(val);
      }
      urlParams.set(key, v);
    }
  });
};

const mergeParamData: (urlParams: any, data: any) => void = (urlParams, data) => {
  const x = { ...data };
  Object.keys(x).forEach((key) => {
    if (urlParams.get(key) !== null && urlParams.get(key) !== undefined) {
      let v = urlParams.get(key);
      if (v && (v[0] === '[' || v[0] === '{')) {
        v = JSON.parse(v);
      }
      if ((['page', 'per_page'].includes(key) || key.endsWith('_id')) && !Number.isNaN(Number(v))) {
        v = Number(v);
      }
      x[key] = v;
    }
  });
  return x;
};

const getStorageKey = (key: string) => `urlParams${key}`;

export default function useUrlParams(defaultParams: any, options: { storage?: boolean } = {}) {
  const storageKey = useRef(getStorageKey(window.location.pathname));
  const initialParams = mergeParamData(getUrlParams(), defaultParams);
  const [value, setValue] = useState<any>(() => {
    if (options.storage) {
      const storedValue = sessionStorage.getItem(storageKey.current);
      if (storedValue) {
        try {
          return JSON.parse(storedValue);
        } catch (e) {
          return initialParams;
        }
      }
    }
    return initialParams;
  });
  const unmounted = useRef<boolean>();
  const isPopstate = useRef<boolean>();
  unmounted.current = false;
  const prevValue = useRef();
  useEffect(() => {
    if ((!prevValue.current || prevValue.current !== value) && !isPopstate.current) {
      const params = getUrlParams();
      mergeUrlParams(params, value || defaultParams);
      window.history.replaceState({ ...params }, '', `#${params.toString()}`);
      if (options.storage) {
        sessionStorage.setItem(storageKey.current, JSON.stringify(value));
      }
    }
    prevValue.current = value;
    isPopstate.current = false;
  }, [defaultParams, value, options.storage]);

  useEffect(() => {
    const updateValue = () => {
      if (!unmounted.current) {
        const data = mergeParamData(getUrlParams(), value);
        isPopstate.current = true;
        setValue(data);
      }
    };
    window.addEventListener('popstate', updateValue);
    return () => {
      unmounted.current = true;
      window.removeEventListener('popstate', updateValue);
    };
  }, [value]);

  useEffect(() => {
    const updateValue = () => {
      const data = mergeParamData(getUrlParams(), value);
      setValue(data);
    };
    window.addEventListener('hashchange', () => {
      updateValue();
    });
    return () => {
      window.removeEventListener('hashchange', updateValue);
    };
  }, [value]);

  const updateParams = useCallback(
    (updatedParams: any) => {
      setValue((prev: any) => {
        const newValue = {
          ...prev,
          ...updatedParams,
        };
        if (options.storage) {
          sessionStorage.setItem(storageKey.current, JSON.stringify(newValue));
        }
        return newValue;
      });
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setValue, options.storage]
  );

  return [value, setValue, updateParams];
}
